import { RootState } from '@Common/models/redux/store';
import { useRouter } from './useRouter';
import { useEffect, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { AsyncStatusEnum } from '../types/common';
import { useLatestRef } from './useLatestRef';

export interface RedirectRequestQuery {
  referrer?: string;
  returnUrl?: string;
}
/**
 * `usePrivateRoute` is usefull for simple routing protectection where only protection required is user being authenticated
 * Note: This doesn't make use of isSyncWithDB it just there to check and route to '/login' if not authenticated
 * @param shouldRedirectToLogin default to `true` i.e if user is not authenticated then go to '/login'
 */
export const usePrivateRoute = (shouldRedirectToLogin = true) => {
  const router = useRouter();
  const isAuthenticated = useSelector(
    (state: RootState) => state.user?.isAuthenticated || false
  );

  const redirect = useCallback(
    (to = '/login', query?: RedirectRequestQuery) =>
      router.push({
        pathname: to,
        query: {
          referrer: query?.referrer || '',
          returnUrl: query?.returnUrl || router.pathname,
        },
      }),
    []
  );

  useEffect(() => {
    if (shouldRedirectToLogin && !isAuthenticated) {
      redirect();
    }
  }, [shouldRedirectToLogin, redirect]);

  return {
    isAuthenticated,
    redirect,
  };
};

/**
 * `useProtectedRoute` is usable for multiple rote protection strategies from private routing to protecting on mobile with auth required
 *
 * @param strategy A function that clousres over sorrudning and must return state which need to synced for route protection
 * The function protect route and user of this hook must route in case of stategy rejection
 *
 * ### Note: Prefer to memo() its renders as this will add to renders by quite 3-4 times which is dependent on number of state breaks in strategy function and number of closure state
 *
 * Usage:
 * ```
 * const router = useRouter();
 * const isAuthenticated = useSelector((state: RootState) => state.user?.isAuthenticated || false);
 * const isSyncWithDB = useSelector((state: RootState) => state.user.isSyncWithDB || false);
 * const isMobile = useSelector((state: RootState) => state.common.platform.isMobile || false);
 *
 * const [status] = useProtectedRote(() => {
 *  if (isMobile) {
 *    return AsyncStatusEnum.REJECT;
 *  }
 *  if (!isSyncWithDB) {
 *    return AsyncStatusEnum.PENDING;
 *  }
 *  if (isAuthenticated) {
 *    return AsyncStatusEnum.RESOLVE;
 *  }
 *
 *  return AsyncStatusEnum.REJECT;
 * });
 *
 * useEffect(() => {
 *  // auth required
 *  if (status === AysncStatusEnum.REJECT && !isMobile) {
 *    return router.push({ to: '/login', query: { returnUrl: router.pathname, referrer: 'cowriter' }});
 *  }
 * }, [status, isMobile]);
 *
 * if (status === AsyncStatusEnum.REJECT && isMobile) {
 *  return <NotSuuportedPage message="Not supported on mobile" />
 * }
 *
 * if (status === AsyncStatusEnum.RESOLVE) {
 *  return <CowriterProduct />
 * }
 *
 * return <CowriterLoader />
 *```
 */
export const useProtectedRoute = (
  strategy: () => AsyncStatusEnum,
  init = AsyncStatusEnum.PENDING
) => {
  const [status, setStatus] = useState<AsyncStatusEnum>(init);
  const callbackRef = useLatestRef(strategy);
  useEffect(() => {
    setStatus(callbackRef.current());
  });
  return [status, setStatus];
};
